import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ClientProvider } from "@/context/ClientContext";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Contacts from "@/pages/Contacts";
import CustomerDetail from "@/pages/CustomerDetail";
import CustomerDuplicates from "@/pages/CustomerDuplicates";
import POCs from "@/pages/POCs";
import Leads from "@/pages/Leads";
import LeadsDashboard from "@/pages/LeadsDashboard";
import LeadSettings from "@/pages/LeadSettings";
import LeadDetail from "@/pages/LeadDetail";
import GenesysScreenPop from "@/pages/GenesysScreenPop";
import GenesysCallback from "@/pages/GenesysCallback";
import KnowledgeBase from "@/pages/KnowledgeBase";
import KnowledgeBaseEditor from "@/pages/KnowledgeBaseEditor";
import KnowledgeBaseArticle from "@/pages/KnowledgeBaseArticle";
import KnowledgeBaseCategories from "@/pages/KnowledgeBaseCategories";
import KnowledgeBaseAnalytics from "@/pages/KnowledgeBaseAnalytics";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import DeptResolve from "@/pages/DeptResolve";
import CsatSurvey from "@/pages/CsatSurvey";
import KbPublic from "@/pages/KbPublic";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-400 text-sm">
        Loading Kippu...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ClientProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/dept/resolve/:token" element={<DeptResolve />} />
            <Route path="/csat/:token" element={<CsatSurvey />} />
            <Route path="/kb/public/:token" element={<KbPublic />} />
            <Route path="/genesys/callback" element={<GenesysCallback />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="tickets/:id" element={<TicketDetail />} />
              <Route path="contacts" element={<Navigate to="/customers" replace />} />
              <Route path="customers" element={<Contacts />} />
              <Route path="customers/duplicates" element={<CustomerDuplicates />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="pocs" element={<POCs />} />
              <Route path="leads" element={<Leads />} />
              <Route path="leads/dashboard" element={<LeadsDashboard />} />
              <Route path="leads/settings" element={<LeadSettings />} />
              <Route path="leads/screen-pop" element={<GenesysScreenPop />} />
              <Route path="leads/:id" element={<LeadDetail />} />
              <Route path="knowledge-base" element={<KnowledgeBase />} />
              <Route path="knowledge-base/new" element={<KnowledgeBaseEditor />} />
              <Route path="knowledge-base/categories" element={<KnowledgeBaseCategories />} />
              <Route path="knowledge-base/analytics" element={<KnowledgeBaseAnalytics />} />
              <Route path="knowledge-base/:id/edit" element={<KnowledgeBaseEditor />} />
              <Route path="knowledge-base/:id" element={<KnowledgeBaseArticle />} />
              <Route path="automations" element={<Navigate to="/settings" replace />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
        </ClientProvider>
      </AuthProvider>
    </div>
  );
}
